import Icon from '@ant-design/icons/es'
import SearchOutlined from '@ant-design/icons/lib/icons/SearchOutlined'
import {Button, Input, Modal, Skeleton, Space, Spin, Typography} from 'antd'
import {doc_undoContentById_Api} from 'apis'
import classNames from 'classnames'
import DidOnMount from 'components/DidOnMount'
import ImageLoader from 'components/ImageLoader'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {LoginContext} from 'components/LoginContext'
import Pure from 'components/Pure'
import ScrollToViewByHash from 'components/ScrollToViewByHash'
import UseState from 'components/UseState'
import getLinkToDetail from 'helpers/getLinkToDetail'
import _ from 'lodash'
import Mark from 'mark.js'
import AsyncAction from 'modules/asyncCache/components/AsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useState,} from 'react'
import {BsChevronRight, BsDash, BsDiamondFill, BsFileEarmarkArrowDown, BsPencilSquare, BsPlusLg,} from 'react-icons/bs'
import {FaAngleDoubleRight} from 'react-icons/fa'
import {RiFontSize} from 'react-icons/ri'
import {Link} from 'react-router-dom'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {paths} from 'views/MainPage/contains'
import {renderLoading} from 'views/MainPage/functions/renderPagingLoading'
import {renderElse, renderIf, renderSelf} from 'views/Shared'
import Description, {mapDocuments, mapKeywords, mapReferenceLinks,} from '../../../components/Feed/Description'
import KeywordAddLink from '../../Keyword/KeywordAddLink'
import KeywordCreation from '../../Keyword/KeywordCreation'
import createConfirmDialog from '../../AppLocals/factory/createConfirmDialog'
import {AdvancedActionGroups} from '../../NewTemplate/components/AdvancedActionGroups'
import DocumentContext from '../DocumentContext'
import ParagraphEditForm from '../EditForms/ParagraphEditForm'
import {ParagraphInsertForm} from '../EditForms/ParagraphInsertForm'
import ParagraphRemoveIndexForm from '../EditForms/ParagraphRemoveForm'
import DocumentLazyList from './DocumentLazyList'
import {decryptContent} from "../../../helpers/dataHelpers";

export const document_row_actions = {
  add_keyword: {
    id: 'add_keyword',
    title: 'add keyword',
    label: 'add',
    type: 'string',
    danger: false,
    description: 'add new keyword document description',
  },
  add_link: {
    id: 'add_link',
    title: 'add document link',
    label: 'add',
    type: 'string',
    danger: false,
    description: 'add new links document description',
  },
  insert_paragraph: {
    id: 'insert_paragraph',
    title: 'insert paragraph',
    label: 'insert',
    type: 'paragraph',
    danger: false,
    description: 'insert paragraph document description',
  },
  edit_paragraph: {
    id: 'edit_paragraph',
    title: 'edit paragraph',
    label: 'edit',
    type: 'paragraph',
    description: 'edit content document description',
  },
  remove_paragraph: {
    id: 'remove_paragraph',
    title: 'remove paragraph',
    label: 'remove',
    type: 'paragraph',
    danger: true,
    description: 'delete content document description',
  },
}

const fontSizeOptions = [
  {
    label: 'Aa',
    value: 'normal',
    className: 'text-doc-sm',
  },
  {
    label: 'Aa',
    value: 'large',
    className: 'text-doc-base',
  },
  {
    label: 'Aa',
    value: 'x-large',
    className: 'text-doc-xl',
  },
]

const headings = {
  h: {
    Component: 'h2',
    className: 'font-bold',
  },
  h1: {
    Component: 'h1',
    className: 'text-2xl font-bold',
  },
  h2: {
    Component: 'h2',
    className: 'text-2xl font-medium',
  },
  h3: {
    Component: 'h3',
    className: 'text-xl font-bold pl-1',
  },
  h4: {
    Component: 'h4',
    className: 'text-base font-extrabold pl-1',
  },
  h5: {
    Component: 'h5',
    className: 'text-base font-medium pl-2 italic',
  },
  h6: {
    Component: 'h6',
    className: 'text-base font-normal pl-3',
  },
  h7: {
    Component: 'h6',
    className: 'text-base font-normal pl-3 italic',
  },
  h8: {
    Component: 'h6',
    className: 'text-sm font-normal pl-4',
  },
  h9: {
    Component: 'h6',
    className: 'text-sm font-normal pl-4 italic',
  },
  h10: {
    Component: 'h6',
    className: 'text-xs font-normal pl-5',
  },
}

export const DocumentRowTypes = Object.freeze({
  HTML: { label: 'paragraph', value: 'p' },
  HEADER: { label: 'header', value: 'h' },
  HEADER_1: { label: 'part', value: 'h1' },
  HEADER_2: { label: 'chapter', value: 'h2' },
  HEADER_3: { label: 'section', value: 'h3' },
  HEADER_4: { label: 'article', value: 'h4' },
  HEADER_5: { label: 'clause', value: 'h5' },
  HEADER_6: { label: 'point', value: 'h6' },
  PARAGRAPH: { label: 'html', value: 'html' },
})

const ContentWrapper = ({
  docId,
  style,
  content,
  children,
  isLoading = false,
}) => {
  const [hasError, setError] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (_.isEmpty(content)) {
        setError(true)
      }
    }, 5000)
    return () => clearTimeout(timer);
  }, [content])
  if (hasError) return null
  if (!!isLoading || _.isEmpty(content)) {
    return renderLoading()
  }
  return (
    <div
      id={docId}
      className="p-3 background-100 leading-7 md:text-doc-base text-doc-sm w-full">
      <div style={style}>{children}</div>
    </div>
  )
}

export const DeferredRender = ({ children, idleTimeout }) => {
  const [render, setRender] = React.useState(false)

  React.useEffect(() => {
    if (render) setRender(false)
    const id = requestIdleCallback(() => setRender(true), {
      timeout: idleTimeout,
    })
    return () => cancelIdleCallback(id)
  }, [idleTimeout])

  if (!render) return null

  return children
}

const AutoScroll = React.memo(() => (
  <DelayRender lazy={true}>
    <ScrollToViewByHash
      transform={(value) => value}
      options={{ behavior: 'smooth' }}>
      <div style={{ width: 1, visibility: 'hidden' }} />
    </ScrollToViewByHash>
  </DelayRender>
))

const DocumentRowContent = ({
  id,
  style,
  content,
  footer,
  className,
  refs = [],
  keywords = [],
  Wrapper = React.Fragment,
  renderContent = renderSelf,
}) => {
  const body = useMemo(() => {
    if (_.isEmpty(content)) {
      return null
    }
    return renderContent(mapDocuments(mapKeywords(content, keywords), refs))
  }, [content, keywords, refs])
  return renderElse(
    _.isEmpty(content),
    <Wrapper
      id={id}
      className={classNames('active-container anchor-offset')}>
      <Description
        style={style}
        className={className}>
        {body}
      </Description>
      {footer}
    </Wrapper>
  )
}

export const DocumentRow = ({
  item,
  hash,
  canEdit = false,
  render = (child, params) => child,
}) => {
  let _style = {}

  const isDeleted = Boolean(item?.deleted)

  const keywords = Array.from(item?.keywords ?? [])

  const content = item?.edited_value ?? item?.value

  const row = useMemo(() => {
    const deleted_highlight = canEdit && isDeleted && 'text-line-through'
    switch (item?.type) {
      case 'img':
        return renderIf(
          _.has(item, 'url'),
          <div className="py-4 w-full">
            <img
              className="w-full object-cover bg-no-repeat"
              src={item?.url}
              alt={item?.alt ?? ''}
            />
            {renderIf(
              _.has(item, 'value'),
              <div className="flex text-color-300 italic">{item?.value}</div>
            )}
          </div>
        )
      case 'html':
        return renderIf(
          _.has(item, 'value'),
          <div className="py-2 w-full flex">
            <div
              className="flex-1 fix-table w-0 overflow-auto"
              dangerouslySetInnerHTML={{
                __html: _.get(item, 'value'),
              }}
            />
          </div>
        )
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
      case 'h7':
      case 'h8':
      case 'h9':
      case 'h10':
      case 'h':
        const { Component = 'h2', className = 'font-bold' } =
          _.get(headings, item?.type) || {}
        return (
          <DocumentRowContent
            id={hash}
            style={_style}
            content={content}
            Wrapper={Component}
            keywords={keywords}
            refs={item?.document_refs}
            className={classNames(
              'py-1 leading-relaxed',
              deleted_highlight,
              className
            )}
          />
        )
      case 'embed':
        return (
          <DocumentRowContent
            id={hash}
            Wrapper="div"
            style={_style}
            content={content}
            keywords={keywords}
            refs={item?.document_refs}
            className={classNames(
              'p-1 background-200 border-l italic',
              deleted_highlight
            )}
            renderContent={(value) =>
              mapReferenceLinks(value, item?.reference_links)
            }
          />
        )
      default:
        return (
          <DocumentRowContent
            id={hash}
            Wrapper="div"
            style={_style}
            keywords={keywords}
            refs={item?.document_refs}
            content={item?.edited_value ?? item?.value}
            className={classNames(
              'py-1 italic text-doc-xs ml-5',
              deleted_highlight
            )}
            renderContent={(value) =>
              mapReferenceLinks(value, item?.reference_links)
            }
          />
        )
    }
  }, [item, hash, canEdit, isDeleted])

  return render(row, item)
}

export const DocumentDetail = ({ item, image, content: raw_content }) => {
  const t = useTranslate()
  const login = useContext(LoginContext)
  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const [fontSize, setFontSize] = useState(1)

  const [selectedText, setSelectedText] = useState('')

  const { isSm, editableLayout } = useContext(LayoutContext)

  const [content, setContent] = useState(() => raw_content)

  const {
    title,
    issued_status,
    expired_date,
    others = [],
    keywords = [],
    categories = [],
    id: document_id,
  } = item || {}

  const isSystemAdmin = !!_.get(login, 'edit_system')

  const canEdit = !!isSystemAdmin && !!editableLayout

  const docId = `${document_id}_display`

  useEffect(() => {
    const _raw_content = decryptContent(raw_content, item)
    if (canEdit) {
      const _pre_content = decryptContent(item?.pre_content_data, item)
      setContent(_pre_content ?? _raw_content)
    } else {
      setContent(_raw_content)
    }
  }, [canEdit, raw_content, item.pre_content_data])

  const modal_content = useMemo(() => {
    const row = action?.value

    const paragraph_id = row?.id

    const paragraph_type = row?.type

    const paragraph_value = row?.edited_value ?? row?.value

    const onCancel = () => {
      setAction({})
    }

    const renderAction = (type) => {
      switch (type) {
        case document_row_actions.add_link.id:
          return (
            <div className="flex flex-col">
              <KeywordAddLink
                onClose={onCancel}
                keyword={selectedText}
                document_id={document_id}
              />
            </div>
          )
        case document_row_actions.insert_paragraph.id:
          return (
            !!paragraph_value && (
              <div className="flex flex-col space-y-3">
                <ParagraphInsertForm
                  onClose={onCancel}
                  document_id={document_id}
                  paragraph_id={paragraph_id}
                  paragraph_type={paragraph_type}
                  paragraph_value={paragraph_value}
                  renderPreview={(__) => (
                    <div className="p-2 border rounded-md">
                      <DocumentRow item={row} />
                    </div>
                  )}
                />
              </div>
            )
          )
        case document_row_actions.edit_paragraph.id:
          return (
            !!paragraph_value && (
              <div className="flex flex-col space-y-3">
                <ParagraphEditForm
                  onClose={onCancel}
                  document_id={document_id}
                  paragraph_id={paragraph_id}
                  paragraph_type={paragraph_type}
                  paragraph_value={paragraph_value}
                  renderPreview={(__) => (
                    <div className="p-2 border rounded-md">
                      <DocumentRow item={row} />
                    </div>
                  )}
                />
              </div>
            )
          )
        case document_row_actions.remove_paragraph.id:
          return (
            !!paragraph_value && (
              <div className="flex flex-col space-y-3">
                <ParagraphRemoveIndexForm
                  onClose={onCancel}
                  document_id={document_id}
                  paragraph_id={paragraph_id}
                  paragraph_value={paragraph_value}
                  renderPreview={(__) => (
                    <div className="p-2 border rounded-md">
                      <DocumentRow item={row} />
                    </div>
                  )}
                />
              </div>
            )
          )
        case document_row_actions.add_keyword.id:
          return (
            <div className="flex flex-col">
              <KeywordCreation
                onClose={onCancel}
                keyword={selectedText}
                document_id={document_id}
              />
            </div>
          )
        default:
          return null
      }
    }

    return (
      <Modal
        width={620}
        className="custom-modal"
        footer={null}
        title={
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('edit document')}
          </div>
        }
        onCancel={onCancel}
        maskClosable={true}
        destroyOnHidden={true}
        open={action?.type}>
        <UseState initialValue={null}>
          {([selectedAction, setSelectedAction]) => {
            const editable = [
              document_row_actions.add_keyword.id,
              document_row_actions.add_link.id,
            ].includes(selectedAction)
            return (
              <React.Fragment>
                <div className="p-3 border rounded-lg flex flex-col mb-4">
                  <span className="text-xs text-color-400 italic leading-tight mb-2">
                    {`${t('content selected')} : `}
                  </span>
                  <Typography.Text
                    className="font-bold text-color-000 ml-2"
                    editable={
                      editable
                        ? {
                            onChange: (value) => setSelectedText(value),
                            icon: <BsPencilSquare className="ml-1" />,
                            tooltip: t('click here to edit'),
                          }
                        : false
                    }>
                    {selectedText || paragraph_value}
                  </Typography.Text>
                </div>
                {selectedAction ? (
                  renderAction(selectedAction)
                ) : (
                  <AdvancedActionGroups.ActionGroup
                    title="edit"
                    description="edit information document">
                    {[document_row_actions.edit_paragraph].map((e, i) => (
                      <AdvancedActionGroups.SimpleActionButton
                        key={i}
                        title={e.title}
                        label={e.label}
                        suffix={<BsChevronRight size={11} />}
                        onClick={() => {
                          setSelectedAction(e.id)
                        }}
                        description={e.description}
                      />
                    ))}
                    {[
                      document_row_actions.insert_paragraph,
                      document_row_actions.add_keyword,
                      document_row_actions.add_link,
                      document_row_actions.remove_paragraph,
                    ].map((e, i) => (
                      <AdvancedActionGroups.SimpleActionWrapper
                        key={i}
                        title={e.title}
                        description={e.description}>
                        <Button
                          size="small"
                          type={'primary'}
                          danger={e.danger}
                          className="text-xs font-medium no-shadow no-text-shadow rounded-full px-4"
                          onClick={() => {
                            setSelectedAction(e.id)
                          }}>
                          {t(e.label)}
                        </Button>
                      </AdvancedActionGroups.SimpleActionWrapper>
                    ))}
                    <div className="h-3" />
                  </AdvancedActionGroups.ActionGroup>
                )}
              </React.Fragment>
            )
          }}
        </UseState>
      </Modal>
    )
  }, [action, document_id, selectedText])

  const renderRow = useCallback(
    (widget, row) => {
      if (canEdit) {
        return (
          <div
            className="hover-container"
            tabIndex={0}>
            <div className="hover-front">
              <div className="speech-bubble flex flex-col background rounded-md shadow-md">
                <div className="pb-1 text-xs text-color-300 italic">
                  {t('click here to edit')}
                </div>
                <div className="flex justify-end gap-2">
                  {[
                    {
                      name: 'undo',
                      label: t('undo'),
                      invisible: !row?.deleted,
                      className: 'text-white bg-orange-500 hover:bg-orange-600',
                      render: ({ className }) => (
                        <AsyncAction
                          onError={notifyOnError(t)}
                          apiInfo={doc_undoContentById_Api}
                          query={bindQueryParam({ id: document_id })}
                          onSuccess={() => {
                            successNotify(
                              NotificationActionTypes.INFORMATION,
                              t
                            )
                          }}>
                          {({ handleAsyncAction, isLoading = false }) => (
                            // <ConfirmPopupAction
                            //   title="are you sure"
                            //   okText={t('yes')}
                            //   cancelText={t('no')}
                            //   onConfirm={() => {
                            //     handleAsyncAction({
                            //       paragraph_id: row?.id,
                            //     })
                            //   }}>
                            <Spin spinning={!!isLoading}>
                              <div
                                className={classNames(className)}
                                onClick={() => {
                                  handleAsyncAction({
                                    paragraph_id: row?.id,
                                  })
                                }}>
                                {t('undo')}
                              </div>
                            </Spin>
                            // </ConfirmPopupAction>
                          )}
                        </AsyncAction>
                      ),
                    },
                    {
                      name: 'edit',
                      label: t('edit'),
                      className:
                        'text-white bg-primary hover:button-bg-primary',
                      onClick: () => {
                        setAction({
                          type: 'start-edit-document',
                          value: row,
                        })
                      },
                    },
                  ].map(
                    (
                      {
                        label,
                        onClick,
                        className,
                        invisible,
                        render = ({ label, ...props }) => (
                          <div {...props}>{label}</div>
                        ),
                        ...rest
                      },
                      index
                    ) =>
                      renderElse(
                        invisible,
                        <React.Fragment key={index}>
                          {render({
                            label,
                            onClick,
                            style: {
                              padding: '2px 10px',
                            },
                            className: classNames(
                              'flex items-center rounded-full',
                              'font-medium text-xs italic cursor-pointer',
                              className
                            ),
                            ...rest,
                          })}
                        </React.Fragment>
                      )
                  )}
                </div>
              </div>
            </div>
            <div className="hover-back cursor-pointer">{widget}</div>
          </div>
        )
      } else {
        return widget
      }
    },
    [canEdit, document_id]
  )

  const marker_class = 'marker-highlight'

  const debounceSearch = _.debounce(function (textToSearch) {
    const markInstance = new Mark(document.getElementById(docId))
    markInstance.unmark({
      done: () => {
        markInstance.mark(textToSearch, {
          className: classNames(marker_class, 'anchor-offset'),
        })
      },
    })
  }, 300)

  return (
    <React.Fragment>
      <div className="flex flex-col space-y-3">
        <div className="flex flex-col md:flex-row gap-3 px-2 mt-3">
          {!!!issued_status ? (
            <div
              style={{ marginTop: 'auto' }}
              className="flex flex-col gap-1 flex-1 items-end md:items-start">
              <span
                style={{ padding: '2px 15px' }}
                className="w-fit rounded-full border border-primary font-bold text-xs text-primary italic">
                {t('drafting')}
              </span>
            </div>
          ) : expired_date ? (
            <div
              style={{ marginTop: 'auto' }}
              className="flex flex-col gap-1 flex-1 items-end md:items-start">
              <span
                style={{ padding: '2px 15px', borderRadius: '999px' }}
                className="w-fit border border-red-500 font-bold text-xs text-red-500 italic">
                {t('expires')}
              </span>
            </div>
          ) : (
            <div className="flex-1" />
          )}
          <div className="flex justify-end gap-3">
            <div className="flex flex-col custom-input-codi">
              <label
                title={t('search on page')}
                className="text-doc-2xs font-normal text-color-300">
                {t('search')}
              </label>
              <UseState initialValue={0}>
                {([match_index, set_match_index]) => (
                  <Input
                    allowClear={true}
                    style={{ width: isSm ? 150 : 180 }}
                    className="rounded-full"
                    placeholder={t('type to search')}
                    suffix={<SearchOutlined />}
                    onKeyPress={(event) => {
                      const array =
                        document.getElementsByClassName(marker_class) ?? []
                      if (array.length > 0 && event.key === 'Enter') {
                        const _current = array[match_index]
                        const element =
                          _current?.parentElement?.parentElement ??
                          _current?.parentElement ??
                          _current
                        if (element) {
                          element.scrollIntoView()
                          match_index++
                          set_match_index(
                            match_index < array.length ? match_index : 0
                          )
                        }
                      }
                    }}
                    onChange={(e) => {
                      set_match_index(0)
                      debounceSearch(e.target.value)
                    }}
                  />
                )}
              </UseState>
            </div>
            <DocumentDownloadAction
              docId={docId}
              item={item}
            />
            <DocumentSorter
              onChange={(value) => {
                if (value === 1) {
                  setFontSize(fontSize + 0.1)
                } else {
                  setFontSize(fontSize - 0.1)
                }
              }}
            />
          </div>
        </div>
        <DelayRender lazy={true}>
          <div className="space-y-3">
            <div className="flex overflow-hidden">
              <div className="break-words flex-1 flex-col verticalList__small px-3">
                <h1 className="font-bold text-primary text-doc-xl">{title}</h1>
                <div className="text-doc-xs flex flex-wrap gap-x-3 gap-y-1">
                  {categories.map((e) => (
                    <Link
                      key={e.id}
                      className="no-underline flex items-center"
                      to={
                        paths.categoryPath
                          .replace(':category', e.id)
                      }>
                      <BsDiamondFill
                        size={5}
                        className="mr-1"
                      />
                      <span className="italic font-medium text-doc-xs">
                        {e.name}
                      </span>
                    </Link>
                  ))}
                </div>
                {!!image && (
                  <div
                    style={{
                      width: '100%',
                      overflow: 'hidden',
                      margin: 'auto',
                      position: 'relative',
                      display: 'inline-block',
                    }}>
                    <ImageLoader
                      alt={
                        (keywords || []).reduce(function (r, e) {
                          r += e.keyword + ', '
                          return r
                        }, '') + 'by Kristo Network'
                      }
                      className="object-contain w-full h-full"
                      style={{
                        verticalAlign: 'middle',
                      }}
                      src={image}
                    />
                  </div>
                )}
              </div>
            </div>

            {others.slice(0, 2).map((e, i) => (
              <h3 key={i}>
                <Link
                  key={i}
                  to={{
                    pathname: getLinkToDetail(e),
                  }}
                  className="px-2 pt-2 flex items-start justify-start text-color-300 background-100 leading-5 md:text-doc-base text-doc-lg">
                  <span className="px-2">
                    <FaAngleDoubleRight />
                  </span>
                  {_.truncate(_.get(e, 'overview.title'), {
                    length: 160,
                  })}
                </Link>
              </h3>
            ))}
            <ContentWrapper
              style={{
                fontSize: fontSize + 'em',
              }}
              docId={docId}
              content={content}>
              <DocumentContext.Consumer>
                {({
                  rows,
                  hasMore,
                  setRows,
                  scrollRef,
                  setHasMore,
                  createRowHash,
                  set_page_loading,
                }) => {
                  const lazy_load = false
                  const array = Array.from(content ?? [])
                  if (lazy_load) {
                    return (
                      <React.Fragment>
                        <AutoScroll key={_.isEmpty(rows)} />
                        <Pure input={[array, canEdit]}>
                          <DocumentLazyList
                            data={array}
                            onChange={(lasted) => {
                              setRows(_.slice(array, 0, lasted + 1))
                              set_page_loading(false)
                              setHasMore(
                                array.length > 0 &&
                                  Number(lasted) < Math.max(array.length - 1, 0)
                              )
                            }}
                            renderItem={(item) => (
                              <DocumentRow
                                key={item?.id}
                                item={item}
                                canEdit={canEdit}
                                render={renderRow}
                                hash={createRowHash(item?.id)}
                              />
                            )}
                          />
                        </Pure>
                        <div
                          ref={scrollRef}
                          style={{ visibility: 'hidden', height: 0 }}
                        />
                        {!!hasMore && (
                          <Skeleton
                            active={true}
                            loading={true}
                          />
                        )}
                      </React.Fragment>
                    )
                  } else {
                    const limit = 250
                    const rest = _.chunk(array.slice(limit), limit)
                    return (
                      <UseState initialValue={rest.length}>
                        {([busy, setBusy]) => (
                          <Spin spinning={!!busy}>
                            <DidOnMount
                              handler={() => {
                                setRows(content)
                                setHasMore(false)
                                set_page_loading(false)
                              }}
                            />
                            <Pure input={[array, canEdit]}>
                              {array.slice(0, limit).map((item, index) => (
                                <DocumentRow
                                  key={index}
                                  item={item}
                                  canEdit={canEdit}
                                  render={renderRow}
                                  hash={createRowHash(item?.id)}
                                />
                              ))}
                              {rest.map((items, index) => (
                                <DelayRender
                                  key={index}
                                  lazy={true}
                                  time={50 * index + 20}>
                                  {Array.from(items ?? []).map(
                                    (item, index) => (
                                      <DocumentRow
                                        key={index}
                                        item={item}
                                        canEdit={canEdit}
                                        render={renderRow}
                                        hash={createRowHash(item?.id)}
                                      />
                                    )
                                  )}
                                  {renderIf(
                                    index === rest.length - 1,
                                    <React.Fragment>
                                      <DidOnMount
                                        handler={() => {
                                          setBusy(false)
                                        }}
                                      />
                                      <AutoScroll />
                                    </React.Fragment>
                                  )}
                                </DelayRender>
                              ))}
                            </Pure>
                          </Spin>
                        )}
                      </UseState>
                    )
                  }
                }}
              </DocumentContext.Consumer>
            </ContentWrapper>
          </div>
        </DelayRender>
      </div>
      {modal_content}
    </React.Fragment>
  )
}

const DocumentDownloadAction = ({ docId, item }) => {
  const t = useTranslate()
  return (
    <div className="flex flex-col items-center">
      <label
        title={t('download')}
        className="text-doc-2xs font-normal text-color-300">
        {t('download')}
      </label>
      <Icon
        className="p-2 w-fit rounded-full border hover:border-primary text-color-400 hover:text-primary"
        onClick={() => {
          createConfirmDialog({
            icon: null,
            title: (
              <span className="font-semibold text-sm text-color-000 italic">
                {t('download this document')}
              </span>
            ),
            message: (
              <span className="text-xs">
                {t('download document description')}
              </span>
            ),
            okText: t('yes'),
            cancelText: t('no'),
            okButtonProps: {
              type: 'primary',
              className: 'button-rounded-full no-border font-medium',
            },
            cancelButtonProps: {
              className: 'button-rounded-full font-medium',
            },
            onOk: () => {
              let header =
                "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
                "xmlns:w='urn:schemas-microsoft-com:office:word' " +
                "xmlns='http://www.w3.org/TR/REC-html40'>" +
                "<head><meta charset='utf-8'><title>Export HTML to Word Document with JavaScript</title></head><body>"
              let footer = '</body></html>'
              let sourceHTML =
                header + document.getElementById(docId).innerHTML + footer
              let source =
                'data:application/vnd.ms-word;charset=utf-8,' +
                encodeURIComponent(sourceHTML)
              let fileDownload = document.createElement('a')
              document.body.appendChild(fileDownload)
              fileDownload.href = source
              fileDownload.download =
                'document_' + _.get(item, 'document_no') + '.doc'
              fileDownload.click()
              document.body.removeChild(fileDownload)
            },
          })
        }}
        component={BsFileEarmarkArrowDown}
      />
    </div>
  )
}

const DocumentSorter = ({ onChange, options = fontSizeOptions }) => {
  const t = useTranslate()
  const { isSm } = useContext(LayoutContext)

  return renderElse(
    _.isEmpty(options),
    <div className="flex flex-col">
      <label
        title={t('font size')}
        className="text-doc-2xs font-normal text-color-300">
        {t('font size')}
      </label>
      <Space
        className="custom-doc-space border rounded-full"
        style={{ height: 32 }}>
        <Button
          onClick={(e) => {
            onChange(-1)
          }}
          style={{ height: 30 }}
          icon={<BsDash size={13} />}
          className="flex flex-center flex-1 no-shadow no-text-shadow rounded-l-full px-1 no-border"
        />
        <span
          style={{ padding: '0 6px' }}
          className="flex flex-center">
          <RiFontSize
            size={isSm ? 16 : 18}
            className="text-color-100"
          />
        </span>
        <Button
          onClick={(e) => {
            onChange(1)
          }}
          style={{ height: 30 }}
          icon={<BsPlusLg size={13} />}
          className="flex flex-center flex-1 no-shadow no-text-shadow rounded-r-full px-1 no-border"
        />
      </Space>
    </div>
  )
}
